import { useEffect, useMemo, useState } from 'react';
import {
  Boxes,
  FileText,
  GitBranch,
  Info,
  Gauge,
} from 'lucide-react';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Spinner,
  Tabs,
  type TabItem,
} from '../../ui';
import type { KnowledgeGateway } from '../../features/knowledge/knowledgeGateway';
import type {
  EffortInsight,
  KnowledgePackage,
  KnowledgeRevision,
  SpecDocument,
} from '../../features/knowledge/types';
import { docKindLabel, docKindTone } from './format';
import { SpecDocViewer } from './SpecDocViewer';
import { EffortInsightPanel } from './EffortInsightPanel';

interface PackageDetailProps {
  gateway: KnowledgeGateway;
  pkg: KnowledgePackage;
}

type DetailTab = 'overview' | 'documents' | 'effort';

/**
 * Master-detail right pane: shows the selected package's overview + revisions,
 * its spec documents (with an inline viewer), and its Effort Insight. Each data
 * slice is fetched lazily from the gateway when the package changes.
 */
export function PackageDetail({ gateway, pkg }: PackageDetailProps) {
  const [tab, setTab] = useState<DetailTab>('overview');

  const [revisions, setRevisions] = useState<KnowledgeRevision[]>([]);
  const [documents, setDocuments] = useState<SpecDocument[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<SpecDocument | null>(null);
  const [effort, setEffort] = useState<EffortInsight | null>(null);

  const [loading, setLoading] = useState(true);
  const [effortLoading, setEffortLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load revisions + documents whenever the package changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedDoc(null);
    setTab('overview');
    Promise.all([
      gateway.listRevisions(pkg.name),
      gateway.listDocuments(pkg.name, pkg.latestRevisionId),
    ])
      .then(([revs, docs]) => {
        if (cancelled) return;
        setRevisions(revs);
        setDocuments(docs);
        setSelectedDoc(docs[0] ?? null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load package.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gateway, pkg.name, pkg.latestRevisionId]);

  // Lazily load the effort insight the first time the Effort tab is opened.
  useEffect(() => {
    if (tab !== 'effort' || effort || effortLoading) return;
    let cancelled = false;
    setEffortLoading(true);
    gateway
      .getEffortInsight(pkg.name, pkg.latestRevisionId)
      .then((ins) => {
        if (!cancelled) setEffort(ins ?? null);
      })
      .catch(() => {
        if (!cancelled) setEffort(null);
      })
      .finally(() => {
        if (!cancelled) setEffortLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, effort, effortLoading, gateway, pkg.name, pkg.latestRevisionId]);

  // Reset the cached effort when switching packages.
  useEffect(() => {
    setEffort(null);
  }, [pkg.name]);

  const tabs = useMemo<TabItem[]>(
    () => [
      { id: 'overview', label: 'Overview', icon: <Info className="h-4 w-4" /> },
      {
        id: 'documents',
        label: `Documents${documents.length ? ` (${documents.length})` : ''}`,
        icon: <FileText className="h-4 w-4" />,
      },
      { id: 'effort', label: 'Effort Insight', icon: <Gauge className="h-4 w-4" /> },
    ],
    [documents.length],
  );

  return (
    <div className="flex flex-col gap-5">
      {/* Package header */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-3 text-accent">
              <Boxes className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">{pkg.name}</CardTitle>
              {pkg.description && (
                <p className="mt-0.5 text-sm text-zinc-500">{pkg.description}</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {pkg.language && <Badge tone="info">{pkg.language}</Badge>}
            {pkg.specType && <Badge tone="neutral">{pkg.specType}</Badge>}
            {pkg.team && <Badge tone="neutral">{pkg.team}</Badge>}
          </div>
        </CardHeader>
        <CardBody className="pt-0">
          <Tabs
            items={tabs}
            value={tab}
            onChange={(id) => setTab(id as DetailTab)}
          />
        </CardBody>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner label="Loading package" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<Boxes className="h-6 w-6" />}
          title="Couldn't load package"
          description={error}
        />
      ) : tab === 'overview' ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-zinc-500" />
                Revisions ({revisions.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardBody className="flex flex-col gap-2">
            {revisions.length === 0 ? (
              <p className="text-sm text-zinc-500">No revisions found.</p>
            ) : (
              revisions.map((r) => (
                <div
                  key={r.revisionId}
                  className="flex items-center justify-between gap-3 rounded-xl border border-surface-border bg-surface-1 px-4 py-2.5"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-zinc-200">
                      {r.revisionId}
                    </span>
                    {r.isLatest && <Badge tone="success">latest</Badge>}
                    <Badge
                      tone={r.status === 'SUCCESS' ? 'neutral' : 'warning'}
                    >
                      {r.status}
                    </Badge>
                  </div>
                  <div className="text-right text-xs text-zinc-500">
                    {r.createdBy && <span>{r.createdBy} · </span>}
                    {r.createdAt
                      ? new Date(r.createdAt).toLocaleDateString()
                      : '—'}
                  </div>
                </div>
              ))
            )}
          </CardBody>
        </Card>
      ) : tab === 'documents' ? (
        <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
          {/* Document list */}
          <div className="flex flex-col gap-2">
            {documents.length === 0 ? (
              <p className="text-sm text-zinc-500">No documents.</p>
            ) : (
              documents.map((d) => {
                const active = d.path === selectedDoc?.path;
                return (
                  <button
                    key={d.path}
                    type="button"
                    onClick={() => setSelectedDoc(d)}
                    className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
                      active
                        ? 'border-accent/50 bg-surface-3'
                        : 'border-surface-border bg-surface-1 hover:bg-surface-3'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-zinc-100">
                        {d.title}
                      </span>
                      <Badge tone={docKindTone(d.kind)}>
                        {docKindLabel(d.kind)}
                      </Badge>
                    </div>
                    {d.summary && (
                      <p className="mt-1 line-clamp-2 text-xs text-zinc-500">
                        {d.summary}
                      </p>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Viewer */}
          <Card>
            <CardBody>
              <SpecDocViewer
                gateway={gateway}
                packageName={pkg.name}
                doc={selectedDoc}
                revisionId={pkg.latestRevisionId}
              />
            </CardBody>
          </Card>
        </div>
      ) : effortLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner label="Analyzing effort" />
        </div>
      ) : effort ? (
        <EffortInsightPanel insight={effort} />
      ) : (
        <EmptyState
          icon={<Gauge className="h-6 w-6" />}
          title="No effort insight"
          description="An effort analysis isn't available for this package yet."
        />
      )}
    </div>
  );
}

export default PackageDetail;
